import { getAccessToken } from '@/lib/auth-session'
import { useAuthStore } from '@/stores/auth.store'
import type { ApiError, TokenResponse, User } from '@/types'
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

/** Turn FastAPI `detail` (string | validation object[]) into a single readable string. */
function formatFastApiDetail(detail: unknown): string {
  if (detail == null) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg)
        }
        return JSON.stringify(item)
      })
      .filter(Boolean)
      .join('; ')
  }
  if (typeof detail === 'object' && detail !== null) {
    if ('message' in detail && typeof (detail as { message: unknown }).message === 'string') {
      return (detail as { message: string }).message
    }
    if ('msg' in detail) {
      return String((detail as { msg: unknown }).msg)
    }
  }
  return String(detail)
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000

console.log('[api-client] VITE_API_URL:', import.meta.env.VITE_API_URL, '-> using:', API_URL)

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _authRetry?: boolean
}

let refreshRequest: Promise<TokenResponse> | null = null
const REFRESH_LOCK_KEY = 'pelgo:auth:refresh-lock'
const REFRESH_LOCK_TTL_MS = API_TIMEOUT + 10_000
const REFRESH_LOCK_WAIT_MS = REFRESH_LOCK_TTL_MS + 5_000

interface RefreshLock {
  owner: string
  expiresAt: number
}

function readRefreshLock(): RefreshLock | null {
  try {
    const value = localStorage.getItem(REFRESH_LOCK_KEY)
    return value ? (JSON.parse(value) as RefreshLock) : null
  } catch {
    return null
  }
}

async function waitForStorageChange(timeoutMs: number): Promise<void> {
  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(done, timeoutMs)
    function done(): void {
      window.clearTimeout(timeout)
      window.removeEventListener('storage', onStorage)
      resolve()
    }
    function onStorage(event: StorageEvent): void {
      if (event.key === REFRESH_LOCK_KEY) done()
    }
    window.addEventListener('storage', onStorage)
  })
}

async function withStorageRefreshLock<T>(operation: () => Promise<T>): Promise<T> {
  const owner = crypto.randomUUID()
  const deadline = Date.now() + REFRESH_LOCK_WAIT_MS

  while (Date.now() < deadline) {
    const current = readRefreshLock()
    if (!current || current.expiresAt <= Date.now()) {
      localStorage.setItem(
        REFRESH_LOCK_KEY,
        JSON.stringify({ owner, expiresAt: Date.now() + REFRESH_LOCK_TTL_MS }),
      )
      await new Promise((resolve) => window.setTimeout(resolve, 20 + Math.random() * 30))
      if (readRefreshLock()?.owner === owner) {
        try {
          return await operation()
        } finally {
          if (readRefreshLock()?.owner === owner) localStorage.removeItem(REFRESH_LOCK_KEY)
        }
      }
    }
    await waitForStorageChange(150)
  }
  throw new Error('Timed out waiting to refresh the session')
}

async function withRefreshLock<T>(operation: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return await navigator.locks.request('pelgo-auth-refresh', operation)
  }
  try {
    const probeKey = `${REFRESH_LOCK_KEY}:probe`
    localStorage.setItem(probeKey, '1')
    localStorage.removeItem(probeKey)
  } catch {
    return await operation()
  }
  return await withStorageRefreshLock(operation)
}

/** Clear stored auth and send the user to login. Shared by the axios 401 handler
 * and any hand-rolled `fetch` calls (e.g. SSE streams) that bypass the interceptor. */
export function handleSessionExpired(): void {
  useAuthStore.getState().clearAuth()
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

async function rotateTokens(failedAccessToken: string | null): Promise<TokenResponse> {
  const performRotation = async (): Promise<TokenResponse> => {
    const currentAccessToken = getAccessToken()
    // Another tab may already have rotated this single-use credential while
    // this request waited for the cross-tab lock.
    if (currentAccessToken && (!failedAccessToken || currentAccessToken !== failedAccessToken)) {
      useAuthStore.getState().rotateToken(currentAccessToken)
      return {
        access_token: currentAccessToken,
        token_type: 'bearer',
      }
    }

    const response = await axios.post<TokenResponse>(`${API_URL}/api/v1/auth/refresh`, undefined, {
      timeout: API_TIMEOUT,
      withCredentials: true,
    })
    useAuthStore.getState().rotateToken(response.data.access_token)
    return response.data
  }

  return await withRefreshLock(performRotation)
}

/** Share one refresh flight across Axios, fetch, and streaming request setup. */
export async function refreshAccessToken(failedAccessToken: string | null): Promise<TokenResponse> {
  refreshRequest ??= rotateTokens(failedAccessToken).finally(() => {
    refreshRequest = null
  })
  return await refreshRequest
}

export async function restoreBrowserSession(): Promise<void> {
  try {
    const tokens = await rotateTokens(null)
    const response = await axios.get<User>(`${API_URL}/api/v1/auth/me`, {
      timeout: API_TIMEOUT,
      withCredentials: true,
      headers: { Authorization: `Bearer ${tokens.access_token}` },
    })
    useAuthStore.getState().restoreAuth(response.data, tokens.access_token)
  } catch {
    useAuthStore.getState().clearAuth(false)
  } finally {
    useAuthStore.getState().markInitialized()
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  // OAuth connect endpoints set short-lived HttpOnly verifier cookies that
  // bind Google/Zoom callbacks to the browser that initiated the flow.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  async (error) => {
    return await Promise.reject(error)
  },
)

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const requestUrl = error.config?.url || ''
    const isCredentialEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/otp/') ||
      requestUrl.includes('/auth/refresh')
    const originalRequest = error.config as RetriableRequestConfig | undefined

    if (
      error.response?.status === 401 &&
      !isCredentialEndpoint &&
      originalRequest &&
      !originalRequest._authRetry
    ) {
      originalRequest._authRetry = true
      try {
        const authorization = originalRequest.headers.Authorization
        const failedAccessToken =
          typeof authorization === 'string' ? authorization.replace(/^Bearer\s+/i, '') : null
        const tokens = await refreshAccessToken(failedAccessToken)
        originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`
        return await apiClient(originalRequest)
      } catch {
        handleSessionExpired()
      }
    }

    // FastAPI returns errors in 'detail' field, some APIs use 'message'
    // biome-ignore lint/suspicious/noExplicitAny: FastAPI error response has 'detail' not in ApiError type
    const responseData = error.response?.data as any
    const fromDetail = formatFastApiDetail(responseData?.detail)
    const errorMessage =
      fromDetail ||
      (typeof responseData?.message === 'string' ? responseData.message : '') ||
      error.message ||
      'An error occurred'

    const apiError: ApiError = {
      message: errorMessage,
      statusCode: error.response?.status || 500,
      ...(responseData?.errors && { errors: responseData.errors }),
    }

    return await Promise.reject(apiError)
  },
)

export default apiClient
